import React, { useState, useEffect, useRef, useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';

import { setQuotationParams, cleanComparisonPlans } from '../../../../../actions/searchActions';

import Input from '../../../../../components/V2/input';
import { PinIcon } from '../../../../../assets/icons/Pin.icon';
import { CalendarIcon } from '../../../../../assets/icons/Calendar.incon';
import { getDaysDiff, formatURL, buildSearchWithDestinationCategories } from '../../../../../utils';
import dataLayer from '../../../../../utils/dataLayer';
import { useSearchBarCommon } from '../../../search/hooks/useSearchBarCommon';
import { formatDateBrFromDate, parseDateBrToDate, toISO, parseSearchFromPath } from '../../../search/utils/searchParsing';

import styles from './styles.module.css';
import Dropdown from '../../../../../components/V2/dropdown';
import DatePicker from '../../../../../components/V2/datepicker';
import SearchIcon from '../../../../../components/search/searchbar/SearchIcon';
import { ChevronRightIcon } from '../../../../../assets/icons/ChevronRight.icon';

const SearchBarDesktop = (props) => {
  const [range, setRange] = useState({ startDate: null, endDate: null });
  const [destinationDropdownOpen, setDestinationDropdownOpen] = useState(false);
  const [isDatepickerOpen, setIsDatepickerOpen] = useState(false);
  const dateInputsRef = useRef(null);
  const datepickerRef = useRef(null);
  const { containerRef, isScrolled, containerHeight, computedDestinationName, beginBr, endBr, permanence } = useSearchBarCommon(props.destinations);

  const [userDestinationSlug, setUserDestinationSlug] = useState(null);
  const [userBeginBr, setUserBeginBr] = useState(null);
  const [userEndBr, setUserEndBr] = useState(null);

  const handleOpenDatepicker = useCallback((e) => {
    if (datepickerRef.current && e && datepickerRef.current.contains(e.target)) return;
    if (!isDatepickerOpen) {
      const beginBrValue = userBeginBr != null ? userBeginBr : (beginBr || '');
      const endBrValue = userEndBr != null ? userEndBr : (endBr || '');
      const start = parseDateBrToDate(beginBrValue);
      const end = parseDateBrToDate(endBrValue);
      setRange({ startDate: start, endDate: end });
    }
    setIsDatepickerOpen((prev) => !prev);
  }, [isDatepickerOpen, userBeginBr, userEndBr, beginBr, endBr]);


  useEffect(() => {
    if (!isDatepickerOpen) return undefined;
    const onDocMouseDown = (e) => {
      if (!dateInputsRef.current) return;
      if (!dateInputsRef.current.contains(e.target)) {
        setIsDatepickerOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIsDatepickerOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isDatepickerOpen]);

  const searchPlan = useCallback(() => {
    const { destinationSlug: pathDestSlug, beginDate: pathBegin, endDate: pathEnd } = parseSearchFromPath();

    const beginISO = range.startDate ? toISO(range.startDate) : (pathBegin || '');
    const endISO = range.endDate ? toISO(range.endDate) : (pathEnd || '');
    const destination = userDestinationSlug || pathDestSlug;

    const isValidISO = (s) => /^(\d{4})-(\d{2})-(\d{2})$/.test(s || '');
    if (!isValidISO(beginISO) || !isValidISO(endISO) || !destination) {
      return;
    }

    if (props.cleanComparisonPlans) props.cleanComparisonPlans();

    const search = buildSearchWithDestinationCategories(props.destinations, destination);
    const path = formatURL(
      props.match && props.match.params ? props.match.params : {},
      `/pesquisa/${destination}/${beginISO}/${endISO}`,
      search,
      !search
    );
    if (props.history && props.history.push) props.history.push(path);

    const diffDays = getDaysDiff(new Date(beginISO), new Date(endISO));
    dataLayer.raw({
      event: 'envio_form_cotacao',
      page_category: 'home',
      tipo_envio: 'novo_lead',
      cotacao: {
        destino: destination,
        data_embarque: beginISO,
        data_desembarque: endISO,
        tempo_permanencia: !Number.isNaN(diffDays) ? diffDays : undefined,
      },
    });
    dataLayer.searchSubmitted(destination, beginISO, endISO);
  }, [userDestinationSlug, range.startDate, range.endDate]);

  const displayedDestinationName = (() => {
    if (userDestinationSlug) {
      const found = (props.destinations || []).find((d) => d.slug === userDestinationSlug);
      return found ? found.name : '';
    }
    return computedDestinationName || '';
  })();

  return (
    <div>
      {isScrolled && <div style={{ height: containerHeight }} />}
      <div ref={containerRef} className={[styles.container, isScrolled ? styles.fixed : '', isScrolled ? styles.scrolled : ''].filter(Boolean).join(' ')}>
          <div className={styles.content}>
        <div className={styles.destinationInput}>
          <Input
            icon={<PinIcon />}
            label="Destino:"
            value={displayedDestinationName}
            onClick={() => setDestinationDropdownOpen(!destinationDropdownOpen)}
            className={styles.input}
            disabled={props.isLoading}
            rightIcon={destinationDropdownOpen ? <ChevronRightIcon size={24} color="#007E47" rotate={270} />: <ChevronRightIcon size={24} color="#007E47" rotate={90} />}
          />
          <Dropdown isOpen={destinationDropdownOpen} onClose={() => setDestinationDropdownOpen(false)}>
            {props.destinations && props.destinations.length > 0 && (
              <ul className={styles.destinationList}>
                {props.destinations.map((d) => (
                  <li
                    key={d.id}
                    className={styles.destinationItem}
                    onClick={() => {
                      setUserDestinationSlug(d.slug);
                      setDestinationDropdownOpen(false);
                    }}
                  >
                    {d.name}
                    {(d.slug === 'brasil' || d.slug === 'receptivo') && (
                      <span className={styles.description}>
                        {d.slug === 'brasil' ? 'Para viagens dentro do Brasil' : 'Para quem vem visitar o Brasil'}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Dropdown>
        </div>

        <div className={styles.dateInputs} ref={dateInputsRef}>
          <div className={styles.dateRow} onClick={handleOpenDatepicker}>
            <Input icon={<CalendarIcon />} label="Embarque no Brasil:" value={userBeginBr != null ? userBeginBr : (beginBr || '')} className={styles.input} />
            <div className={styles.separator} />
            <Input icon={<CalendarIcon />} label="Desembarque no Brasil:" value={userEndBr != null ? userEndBr : (endBr || '')} className={styles.input} />
          </div>

          {isDatepickerOpen && (
            <div ref={datepickerRef}>
              <DatePicker
                selectionMode="range"
                value={range}
                onChange={(sel) => setRange(sel)}
                onComplete={(sel) => {
                  setUserBeginBr(formatDateBrFromDate(sel.startDate));
                  setUserEndBr(formatDateBrFromDate(sel.endDate));
                  setIsDatepickerOpen(false); // fecha ao selecionar as datas
                }}
                minDate={new Date()}
                weekStartsOn={7} // semana iniciando na segunda
                numberOfMonths={2}
                locale="pt-BR"
                allowSameDay
              />
            </div>
          )}
        </div>

        <div className={styles.permanence}>
          <div className={styles.permanenceRow}>
            <CalendarIcon />
            <div className={styles.permanenceLabel}>Permanência:</div>
          </div>
          {permanence}
        </div>

        <button className={styles.searchButton} type="button" onClick={searchPlan}>
          <SearchIcon />
        </button>
          </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  destinations: state.global.destinations,
});
export default withRouter(connect(mapStateToProps, {
  setQuotationParams,
  cleanComparisonPlans,
})(SearchBarDesktop));



// WEBPACK FOOTER //
// ./src/views/v2/search/components/searchBarDesktop/index.jsx